import { RefObject, useEffect } from 'react';

/**
 * Runs `onResize` whenever the element behind `ref` changes size, observing it
 * for exactly the lifetime of the component and disconnecting on unmount.
 *
 * Observes a ref rather than a selector so it can serve any element, and
 * tolerates environments without `ResizeObserver` (jsdom) by doing nothing —
 * the caller's own initial layout pass still runs. A real observer also fires
 * once on `observe`, so the caller gets an initial measurement for free.
 */
const useResizeObserver = <T extends Element>(
  ref: RefObject<T>,
  onResize: () => void,
) => {
  useEffect(() => {
    const element = ref.current;

    if (!element || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const observer = new ResizeObserver(() => onResize());
    observer.observe(element);

    return () => observer.disconnect();
  }, [ref, onResize]);
};

export default useResizeObserver;
